const colorPalette = ["red", "blue", "yellow", "green", "purple", "orange"] as const;

export type AvatarPalette = (typeof colorPalette)[number];

const PickPalette = (name: string): AvatarPalette => {
  const index = name.charCodeAt(0) % colorPalette.length;
  return colorPalette[index];
};

/**
 * PDF avatar colors — Chakra Avatar `subtle` variant (colorPalette.muted + .fg).
 * HTML uses <Avatar colorPalette={...} />; keep these in sync with that recipe only.
 */
export const AVATAR_COLORS: Record<AvatarPalette, { bg: string; color: string }> = {
  red: { bg: "#FECACA", color: "#991919" },
  blue: { bg: "#BFDBFE", color: "#173DA6" },
  yellow: { bg: "#FEF08A", color: "#845209" },
  green: { bg: "#BBF7D0", color: "#116932" },
  purple: { bg: "#E9D5FF", color: "#641BA3" },
  orange: { bg: "#FED7AA", color: "#92310A" },
};

export const getAvatarColors = (name: string) =>
  AVATAR_COLORS[PickPalette(name)];

export const getInitials = (name: string): string => {
  const parts = name.trim().split(" ");
  return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase();
};

/**
 * Initials for a signed-in user's display name, which may carry an organisation
 * suffix: "Kim Nguyen (Collab. Court)" -> "KN", "Vinay - Tech Unicorn USA" -> "V".
 * Uses the first and LAST name words so two-part surnames keep the surname.
 */
export const getUserInitials = (displayName: string): string => {
  const person = displayName
    .trim()
    .split(/\s+-\s+/)[0]
    .replace(/\s*\([^)]*\)\s*$/, "")
    .trim();
  const parts = person.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

/**
 * Initials ordered first-name-first even when the display name is "Last, First".
 * "Haas, Anthony" -> "AH".
 */
export const getFirstNameInitials = (displayName: string): string => {
  const trimmed = displayName.trim();
  if (trimmed.includes(",")) {
    const [last, rest] = trimmed.split(",").map((s) => s.trim());
    const first = (rest || "").split(/\s+/)[0] || "";
    return ((first[0] || "") + (last[0] || "")).toUpperCase();
  }
  return getInitials(trimmed);
};

export { PickPalette };
